
// ./src/utils/unitDisplayHelpers.ts
import { MonthlySummaryUnits, TotalSummaryUnits } from "./api";

// ============================================
// Types
// ============================================

/**
 * Unit display mode - which unit system to use for display
 */
export type UnitDisplayMode = "user" | "car";

/**
 * Combined unit info for display purposes
 */
export interface DisplayUnits {
  distanceUnit: string;
  volumeUnit: string;
  consumptionUnit: string;
}

/**
 * Result of checking if units differ between user and car preferences
 */
export interface UnitsDifferResult {
  /** Whether any unit differs */
  differs: boolean;
  /** Which specific units differ */
  distanceDiffers: boolean;
  volumeDiffers: boolean;
  consumptionDiffers: boolean;
}

// ============================================
// Unit Comparison Functions
// ============================================

/**
 * Compare two unit objects to determine if they have different units.
 * Returns detailed information about which units differ.
 */
export const compareUnits = (
  inUserUnits?: MonthlySummaryUnits | TotalSummaryUnits | null,
  inCarUnits?: MonthlySummaryUnits | TotalSummaryUnits | null
): UnitsDifferResult => {
  // If either is missing, assume no difference (can't compare)
  if (!inUserUnits || !inCarUnits) {
    return {
      differs: false,
      distanceDiffers: false,
      volumeDiffers: false,
      consumptionDiffers: false,
    };
  }

  const distanceDiffers = inUserUnits.distanceUnit !== inCarUnits.distanceUnit;
  const volumeDiffers = inUserUnits.volumeUnit !== inCarUnits.volumeUnit;

  // consumptionUnit might be on either type
  const userConsumption = (inUserUnits as any).consumptionUnit;
  const carConsumption = (inCarUnits as any).consumptionUnit;
  const consumptionDiffers =
    userConsumption && carConsumption
      ? userConsumption !== carConsumption
      : false;

  return {
    differs: distanceDiffers || volumeDiffers || consumptionDiffers,
    distanceDiffers,
    volumeDiffers,
    consumptionDiffers,
  };
};

/**
 * Check if units differ for a monthly summary
 */
export const monthlyUnitsdiffer = (
  inUserUnits?: MonthlySummaryUnits | null,
  inCarUnits?: MonthlySummaryUnits | null
): boolean => {
  return compareUnits(inUserUnits, inCarUnits).differs;
};

/**
 * Check if units differ for a total summary
 */
export const totalUnitsDiffer = (
  inUserUnits?: TotalSummaryUnits | null,
  inCarUnits?: TotalSummaryUnits | null
): boolean => {
  return compareUnits(inUserUnits, inCarUnits).differs;
};

// ============================================
// Unit Selection Functions
// ============================================

/**
 * Get the appropriate unit object based on display mode.
 * Falls back to the other if the preferred one is not available.
 */
export const selectUnits = <T extends MonthlySummaryUnits | TotalSummaryUnits>(
  mode: UnitDisplayMode,
  inUserUnits?: T | null,
  inCarUnits?: T | null
): T | null => {
  if (mode === "user") {
    return inUserUnits ?? inCarUnits ?? null;
  }
  return inCarUnits ?? inUserUnits ?? null;
};

/**
 * Get display units info (unit labels only) based on mode
 */
export const getDisplayUnits = (
  mode: UnitDisplayMode,
  inUserUnits?: MonthlySummaryUnits | TotalSummaryUnits | null,
  inCarUnits?: MonthlySummaryUnits | TotalSummaryUnits | null
): DisplayUnits => {
  const selected = selectUnits(mode, inUserUnits, inCarUnits);

  return {
    distanceUnit: selected?.distanceUnit || "km",
    volumeUnit: selected?.volumeUnit || "l",
    consumptionUnit: (selected as any)?.consumptionUnit || "l100km",
  };
};

// ============================================
// Unit Label Formatting
// ============================================

/**
 * Format unit abbreviations for display in toggle button.
 * Returns a compact string like "km · L" or "mi · gal"
 */
export const formatUnitLabel = (
  distanceUnit: string,
  volumeUnit: string
): string => {
  // Normalize volume unit for display
  const volumeLabel = normalizeVolumeLabel(volumeUnit);
  return `${distanceUnit} · ${volumeLabel}`;
};

/**
 * Normalize volume unit codes to human-readable labels
 */
export const normalizeVolumeLabel = (volumeUnit: string): string => {
  switch (volumeUnit) {
    case "l":
      return "L";
    case "gal-us":
      return "gal";
    case "gal-uk":
      return "gal UK";
    default:
      return volumeUnit;
  }
};

/**
 * Normalize distance unit for display
 */
export const normalizeDistanceLabel = (distanceUnit: string): string => {
  switch (distanceUnit) {
    case "km":
      return "km";
    case "mi":
      return "mi";
    default:
      return distanceUnit;
  }
};

// ============================================
// Mode Label Helpers
// ============================================

/**
 * Get a descriptive label for the current mode (for tooltips)
 */
export const getModeLabel = (
  mode: UnitDisplayMode,
  t: (key: string) => string
): string => {
  return mode === "user"
    ? t("vehicle.unitsUserPreference")
    : t("vehicle.unitsCarPreference");
};

/**
 * Get a label describing what switching will do (for tooltips)
 */
export const getSwitchLabel = (
  currentMode: UnitDisplayMode,
  t: (key: string) => string
): string => {
  return currentMode === "user"
    ? t("vehicle.switchToCarUnits")
    : t("vehicle.switchToUserUnits");
};